import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

// models
import { Product } from '../../planogram/models/product';
import { ProductGroup } from '../../planogram/models/product-group';
import { ProductTypes } from '../../product/models/models';

// services
import { ProductsService } from './products.service';
import { ServiceFeatures } from '../models/serviceFeatures';
import { SessionService } from './session.service';

export interface IHighlightOption {
	label: string;
	value: HighlightOptionsValue;
}

export enum HighlightOptionsValue {
	Empty = '',
	ProductHoles = 'product_holes',
	ProductHolesPrediction = 'product_holes_prediction',
	Brands = 'brands',
	Categories = 'categories',
	Suppliers = 'suppliers',
	Doubles = 'doubles',
	ActiveProducts = 'activeProducts',
	InactiveProducts = 'inactiveProducts',
	Status = 'status',
	Format = 'format'
}

@Injectable({
	providedIn: 'root'
})
export class HighlightProductsService {

	private _highlightOptions: Array<IHighlightOption> = [];
	activeProductsColors: { [key: number]: string } = {};
	brandsColors: { [key: number]: string } = {};
	categoriesColors: { [key: number]: string } = {};
	inactiveProductsColors: { [key: number]: string } = {};
	productsStock: Record<string, { stock: number | null; avg_sales?: number | null }> = {};
	private productsStockCacheKey: string = '';
	suppliersColors: { [key: number]: string } = {};
	statusColors: { [key: number]: string } = {};
	formatColors: Map<ProductTypes, string> = new Map<ProductTypes, string>();

	constructor(
		private translate: TranslateService,
		private productsService: ProductsService,
		private sessionService: SessionService
	) {
		if (this.sessionService.hasFeature(ServiceFeatures.ProductStock)) {
			this._highlightOptions.push(
				{
					label: 'HIGHLIGHT_PRODUCTS_HOLES',
					value: HighlightOptionsValue.ProductHoles
				},
				{
					label: 'HIGHLIGHT_PRODUCTS_HOLES_PREDICTION',
					value: HighlightOptionsValue.ProductHolesPrediction
				}
			);
		}

		this._highlightOptions.push({
			label: 'HIGHLIGHT_PRODUCTS_DOUBLES',
			value: HighlightOptionsValue.Doubles
		});

		if (this.sessionService.hasFeature(ServiceFeatures.Brands)) {
			this._highlightOptions.push({
				label: 'HIGHLIGHT_PRODUCTS_BRANDS',
				value: HighlightOptionsValue.Brands
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.Categories)) {
			this._highlightOptions.push({
				label: 'HIGHLIGHT_PRODUCTS_CATEGORIES',
				value: HighlightOptionsValue.Categories
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.Suppliers)) {
			this._highlightOptions.push({
				label: 'HIGHLIGHT_PRODUCTS_SUPPLIERS',
				value: HighlightOptionsValue.Suppliers
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.AssortmentTypes)) {
			this._highlightOptions.push(
				{
					label: 'HIGHLIGHT_ACTIVE_PRODUCTS',
					value: HighlightOptionsValue.ActiveProducts
				},
				{
					label: 'HIGHLIGHT_INACTIVE_PRODUCTS',
					value: HighlightOptionsValue.InactiveProducts
				}
			);
		}

		if (this.sessionService.hasFeature(ServiceFeatures.ProductStatuses)) {
			this._highlightOptions.push({
				label: 'HIGHLIGHT_PRODUCT_STATUSES',
				value: HighlightOptionsValue.Status
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.MasterPositions)) {
			this._highlightOptions.push({
				label: 'HIGHLIGHT_PRODUCTS_FORMAT',
				value: HighlightOptionsValue.Format
			});
		}
	}

	set highlightOptions(highlightOptions: Array<IHighlightOption>) {
		this._highlightOptions = highlightOptions;
	}

	get highlightOptions(): Array<IHighlightOption> {
		return this._highlightOptions;
	}

	applyHighlight(
		products: Array<Product>,
		planogramId: number | Array<number>,
		planId: number
	): { [key in HighlightOptionsValue]: Function } {
		return {
			[HighlightOptionsValue.Empty]: null,
			[HighlightOptionsValue.ProductHoles]: async () => {
				const highlightProducts = [];
				await this.loadProductsStock(products, planogramId, planId);

				Object.keys(this.productsStock).forEach(product => {
					if (this.productsStock[product].stock !== null && this.productsStock[product].stock <= 0) {
						highlightProducts[product] = {
							color: '#ff0000',
							stock: this.productsStock[product].stock
						};
					} else {
						highlightProducts[product] = {
							stock: this.productsStock[product].stock
						};
					}
				});

				return highlightProducts;
			},
			[HighlightOptionsValue.ProductHolesPrediction]: async () => {
				const highlightProducts = [];
				await this.loadProductsStock(products, planogramId, planId);

				Object.keys(this.productsStock).forEach(product => {
					if (
						this.productsStock[product].stock !== null &&
						this.productsStock[product].avg_sales !== null &&
						this.productsStock[product].stock > 0 &&
						this.productsStock[product].avg_sales > 0 &&
						this.productsStock[product].stock / this.productsStock[product].avg_sales < 2
					) {
						highlightProducts[product] = {
							color: '#ffff00',
							stock: this.productsStock[product].stock
						};
					} else {
						highlightProducts[product] = {
							stock: this.productsStock[product].stock
						};
					}
				});

				return highlightProducts;
			},
			[HighlightOptionsValue.Brands]: () => {
				const highlightProducts: { [key: number]: { id: string, brand: string, color: string } } = {};

				products.forEach((product: Product) => {
					highlightProducts[product.id] = {
						id: product.brandsUniqueId,
						brand: product.brandsUniqueName,
						color: this.brandsColors[product.brandsUniqueId]
					};
				});

				return highlightProducts;
			},
			[HighlightOptionsValue.Categories]: () => {
				const highlightProducts: { [key: number]: { id: string, category: string, color: string } } = {};

				products.forEach((product: Product) => {
					highlightProducts[product.id] = {
						id: product.categoriesUniqueId,
						category: product.categoriesUniqueName,
						color: this.categoriesColors[product.categoriesUniqueId]
					};
				});

				return highlightProducts;
			},
			[HighlightOptionsValue.Suppliers]: () => {
				const highlightProducts: { [key: number]: { id: string, supplier: string, color: string } } = {};

				products.forEach((product: Product) => {
					highlightProducts[product.id] = {
						id: product.suppliersUniqueId,
						supplier: product.suppliersUniqueName,
						color: this.suppliersColors[product.suppliersUniqueId]
					};
				});

				return highlightProducts;
			},
			[HighlightOptionsValue.Doubles]: () => {
				const highlightProducts: { [key: number]: { color: string } } = {};

				products.forEach((product: Product) => {
					highlightProducts[product.id] = {
						color: this.getRandomColor()
					};
				});

				return highlightProducts;
			},
			[HighlightOptionsValue.ActiveProducts]: () => {
				const highlightProducts: { [key: number]: { id: number, assortment_type: string, color: string } } = {};

				products
					.filter((product: Product) => product.is_active)
					.forEach((product: Product) => {
						highlightProducts[product.id] = {
							id: product.assortment_type.id,
							assortment_type: product.assortment_type.name,
							color: this.activeProductsColors[product.assortment_type.id]
						};
					});

				return highlightProducts;
			},
			[HighlightOptionsValue.InactiveProducts]: () => {
				const highlightProducts: { [key: number]: { id: number, assortment_type: string, color: string } } = {};

				products
					.filter((product: Product) => !product.is_active)
					.forEach((product: Product) => {
						highlightProducts[product.id] = {
							id: product.assortment_type.id,
							assortment_type: product.assortment_type.name,
							color: this.inactiveProductsColors[product.assortment_type.id]
						};
					});

				return highlightProducts;
			},
			[HighlightOptionsValue.Status]: () => {
				const highlightProducts: { [key: number]: { id: string, status: string, color: string } } = {};

				products
					.filter((product: Product) => product.statusesUniqueId)
					.forEach((product: Product) => {
						highlightProducts[product.id] = {
							id: product.statusesUniqueId,
							status: product.statusesUniqueName,
							color: this.statusColors[product.statusesUniqueId]
						};
					});

				return highlightProducts;
			},
			[HighlightOptionsValue.Format]: () => {
				const highlightProducts: { [key: number]: { id: boolean, format: string, color: string } } = {};

				products
					.forEach((product: Product) => {
						highlightProducts[product.id] = {
							id: product.is_master_position,
							format: this.translate.instant(product.is_master_position ? 'MASTER_POSITION.PLURAL_TITLE' : 'PRODUCTS'),
							color: this.formatColors.get(product.is_master_position ? ProductTypes.MasterPosition : ProductTypes.Product)
						};
					});

				return highlightProducts;
			}
		};
	}

	private buildProductsStockCacheKey(
		products: Array<Product>,
		planogramIds: Array<number>,
		planId: number
	): string {
		return [
			planId ?? '',
			planogramIds.slice().sort((a: number, b: number) => a - b).join(','),
			products.map((product: Product) => product.id).sort((a: number, b: number) => a - b).join(',')
		].join('|');
	}

	private async loadProductsStock(
		products: Array<Product>,
		planogramId: number | Array<number>,
		planId: number
	): Promise<void> {
		const planogramIds: Array<number> = (Array.isArray(planogramId) ? planogramId : [planogramId])
			.filter((id: number) => !!id);
		const cacheKey: string = this.buildProductsStockCacheKey(products, planogramIds, planId);

		if (this.productsStockCacheKey === cacheKey) return;

		if (!planogramIds.length) {
			this.productsStock = {};
			this.productsStockCacheKey = cacheKey;
			return;
		}

		const stocks: Array<Record<string, { stock: number | null; avg_sales?: number | null }>> = await Promise.all(
			planogramIds.map((id: number) => this.productsService.getStandProductsStock(id, planId))
		);

		this.productsStock = stocks.reduce(
			(merged: Record<string, { stock: number | null; avg_sales?: number | null }>, stock: Record<string, { stock: number | null; avg_sales?: number | null }>) => ({
				...merged,
				...stock
			}),
			{}
		);
		this.productsStockCacheKey = cacheKey;
	}

	getCorrectHighlight(value: '' | HighlightOptionsValue, highlight: any, products: Array<ProductGroup>, pg: ProductGroup): any {
		if (value == HighlightOptionsValue.Doubles) {
			const length = products.filter((sp: ProductGroup) => sp.product.id === pg.product.id).length;
			return length > 1 ? highlight[pg.product.id] : {};
		}
		return { ...highlight[pg.product.id] };
	}

	getRandomColor(): string {
		const letters: string = '0123456789ABCDEF';
		let color: string = '#';
		for (let i = 0; i < 6; i++) {
			color += letters[Math.floor(Math.random() * 16)];
		}
		return color;
	}

	setColors(products: Array<Product>): void {
		products.forEach((product: Product) => {
			if (!this.brandsColors.hasOwnProperty(product.brandsUniqueId)) {
				this.brandsColors[product.brandsUniqueId] = this.getRandomColor();
			}
			if (!this.categoriesColors.hasOwnProperty(product.categoriesUniqueId)) {
				this.categoriesColors[product.categoriesUniqueId] = this.getRandomColor();
			}
			if (!this.suppliersColors.hasOwnProperty(product.suppliersUniqueId)) {
				this.suppliersColors[product.suppliersUniqueId] = this.getRandomColor();
			}
			if (!this.activeProductsColors.hasOwnProperty(product.assortment_type.id)) {
				this.activeProductsColors[product.assortment_type.id] = this.getRandomColor();
			}
			if (!this.inactiveProductsColors.hasOwnProperty(product.assortment_type.id)) {
				this.inactiveProductsColors[product.assortment_type.id] = this.getRandomColor();
			}
			if (!this.statusColors.hasOwnProperty(product.statusesUniqueId)) {
				this.statusColors[product.statusesUniqueId] = this.getRandomColor();
			}
		});

		this.formatColors = new Map<ProductTypes, string>([
			[ProductTypes.Product, this.getRandomColor()],
			[ProductTypes.MasterPosition, this.getRandomColor()]
		]);
	}
}